/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useState, useMemo, useCallback } from 'react';
import { Button, Alert } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Table, type ColumnDef } from '@g17eco/molecules/table';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { DebouncedSearchBox } from '@g17eco/molecules/search';
import { Loader } from '@g17eco/atoms/loader';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { RunStatusBadge } from '@apps/company-tracker/components/intelligence/insights-synthesis/partials/RunStatusBadge';
import { InsightProgressBar } from '@apps/company-tracker/components/intelligence/insights-synthesis/partials/InsightProgressBar';
import { SectionTypeBadge } from '@apps/company-tracker/components/intelligence/insights-synthesis/partials/SectionTypeBadge';
import { useListGenerationRunsQuery } from '@api/insights-synthesis';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { GenerationRunStatus, type GenerationRun } from '@g17eco/types/insights-synthesis';
import type { Option } from '@g17eco/molecules/select/types';
import { formatDate, formatHumaniseDate, DATE } from '@utils/date';

interface RunsListProps {
  initiativeId: string;
  selectedBatchId?: string;
}

const STATUS_OPTIONS: Option<GenerationRunStatus>[] = [
  { value: GenerationRunStatus.Pending, label: 'Pending' },
  { value: GenerationRunStatus.Processing, label: 'Processing' },
  { value: GenerationRunStatus.Completed, label: 'Completed' },
  { value: GenerationRunStatus.Failed, label: 'Failed' },
];

const formatDateTime = (dateString: string) => formatDate(dateString, DATE.AT_TIME);

export const RunsList = ({ initiativeId }: RunsListProps) => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState<GenerationRunStatus[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: runs = [],
    isLoading,
    error,
    refetch,
  } = useListGenerationRunsQuery({
    initiativeId,
    status: statusFilter.length > 0 ? statusFilter : undefined,
  });

  const filteredRuns = useMemo(() => {
    if (!searchTerm.trim()) {
      return runs;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return runs.filter((run) => {
      const sectionType = run.config.sectionType?.toLowerCase() ?? 'all sections';
      const status = run.status.toLowerCase();
      return sectionType.includes(lowerSearch) || status.includes(lowerSearch);
    });
  }, [runs, searchTerm]);

  const handleViewRun = useCallback((run: GenerationRun) => {
    history.push(generateUrl(ROUTES.INSIGHTS_SYNTHESIS_RUN, { initiativeId, batchId: run.batchId }));
  }, [history, initiativeId]);

  const columns = useMemo<ColumnDef<GenerationRun>[]>(
    () => [
      {
        accessorKey: 'config.sectionType',
        header: 'Section',
        cell: ({ row }) => <SectionTypeBadge sectionType={row.original.config.sectionType} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <RunStatusBadge status={row.original.status} />,
      },
      {
        id: 'progress',
        header: 'Progress',
        cell: ({ row }) => <InsightProgressBar progress={row.original.progress} />,
        meta: { cellProps: { style: { minWidth: 180 } } },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <SimpleTooltip text={formatDateTime(row.original.createdAt)}>
            <span className='small text-muted'>{formatHumaniseDate(row.original.createdAt)}</span>
          </SimpleTooltip>
        ),
      },
      {
        accessorKey: 'completedAt',
        header: 'Completed',
        cell: ({ row }) =>
          row.original.completedAt ? (
            <SimpleTooltip text={formatDateTime(row.original.completedAt)}>
              <span className='small text-muted'>{formatHumaniseDate(row.original.completedAt)}</span>
            </SimpleTooltip>
          ) : (
            <span className='small text-muted'>-</span>
          ),
      },
      {
        id: 'actions',
        header: '',
        meta: { cellProps: { style: { width: 100 } } },
        cell: ({ row }) => (
          <Button color='outline-primary' size='sm' onClick={() => handleViewRun(row.original)}>
            View <i className='fa fa-chevron-right ms-1' />
          </Button>
        ),
      },
    ],
    [handleViewRun]
  );

  if (error) {
    return (
      <Alert color='danger'>
        Failed to load synthesis runs.{' '}
        <Button color='link' className='p-0' onClick={() => refetch()}>
          Try again
        </Button>
      </Alert>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className='d-flex justify-content-end align-items-center gap-3 mb-3'>
        <div style={{ minWidth: '200px' }}>
          <DebouncedSearchBox placeholder='Search runs...' onTextChange={setSearchTerm} />
        </div>
        <div style={{ minWidth: '200px' }}>
          <SelectFactory<GenerationRunStatus>
            selectType={SelectTypes.MultipleSelect}
            options={STATUS_OPTIONS}
            values={statusFilter}
            onChange={(values) => setStatusFilter(values as GenerationRunStatus[])}
            placeholder='Filter by status...'
            showSelectAll={false}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredRuns}
        pageSize={10}
        responsive
        noData={<div className='text-muted py-4'>No synthesis runs found. Start a new run to generate insights.</div>}
        sortBy={[{ id: 'createdAt', desc: true }]}
      />
    </div>
  );
};
